import { apiBase, isNative, sessionToken } from '../platform/host';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Stable machine-readable code from the backend, used as an i18n key. */
    readonly code: string,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

function readCookie(name: string): string | null {
  // Checked against undefined rather than falsiness so that a cookie legitimately
  // set to an empty string is returned as '' instead of being reported missing.
  const value = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1];
  return value === undefined ? null : decodeURIComponent(value);
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  // FormData must be left alone: the browser has to set the content type itself
  // so that it can include the multipart boundary, and overriding it produces a
  // request the server cannot parse.
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  // Double-submit CSRF: echo back the cookie the server set at login. Only
  // needed for cookie auth — a bearer token cannot be attached by a cross-site
  // form post in the first place.
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie('csrf_token');
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  // The application holds its own session: its page comes from the WebView at
  // `https://localhost`, so a cookie for the server's host would never be sent
  // with these requests even if one had been set. Says which client it is, too,
  // because that is what decides whether the login answer carries a token.
  if (isNative) {
    headers.set('x-client', 'android');
    const token = sessionToken();
    if (token) headers.set('authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(`${apiBase}${path}`, {
      ...init,
      method,
      headers,
      // Cookies belong to the browser copy. Sending them from the application
      // would ask the server for a cross-site cookie it is right to refuse.
      credentials: isNative ? 'omit' : 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'network');
  }

  if (!response.ok) {
    let code = 'unknown';
    try {
      const body = (await response.json()) as { detail?: string };
      if (typeof body.detail === 'string') code = body.detail;
    } catch {
      // Non-JSON error body (a proxy error page, say) — keep 'unknown'.
    }
    throw new ApiError(response.status, code);
  }

  return response;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await send(path, init);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** For endpoints that answer with a file rather than JSON — label PDFs and
 *  rendered previews. Same auth, same CSRF, same error mapping. */
export async function requestBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  return (await send(path, init)).blob();
}

/** A bitmap plus response headers that describe how its server raster was drawn. */
export async function requestBlobWithHeaders(path: string, init: RequestInit = {}): Promise<{ blob: Blob; headers: Headers }> {
  const response = await send(path, init);
  return { blob: await response.blob(), headers: response.headers };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  blob: (path: string) => requestBlob(path),
  blobWithHeaders: (path: string) => requestBlobWithHeaders(path),
  postBlob: (path: string, body: unknown) =>
    requestBlob(path, { method: 'POST', body: JSON.stringify(body) }),
  postBlobWithHeaders: (path: string, body: unknown) =>
    requestBlobWithHeaders(path, { method: 'POST', body: JSON.stringify(body) }),
  /** A multipart post the caller built itself — a file plus fields alongside it,
   *  which `upload` cannot express. */
  postForm: <T>(path: string, body: FormData) => request<T>(path, { method: 'POST', body }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(path, { method: 'POST', body: form });
  },
};
